import React, { useState } from 'react';
import { Layout, Menu, Button, Typography, Avatar, Dropdown } from 'antd';
import {
  DashboardOutlined,
  CopyrightOutlined,
  TeamOutlined,
  HistoryOutlined,
  BarChartOutlined,
  LogoutOutlined,
  UserOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import CouponManagement from './CouponManagement';
import SalesManagement from './SalesManagement';
import RedemptionRecords from './RedemptionRecords';
import Statistics from './Statistics';
import Settings from './Settings';
import Footer from '../Common/Footer';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

const AdminDashboard: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 根据当前路径确定选中的菜单项
  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === '/admin' || path === '/admin/') return '/admin';
    if (path.startsWith('/admin/coupons')) return '/admin/coupons';
    if (path.startsWith('/admin/sales')) return '/admin/sales';
    if (path.startsWith('/admin/records')) return '/admin/records';
    if (path.startsWith('/admin/statistics')) return '/admin/statistics';
    if (path.startsWith('/admin/settings')) return '/admin/settings';
    return '/admin';
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenu = (
    <Menu>
      <Menu.Item key="profile" icon={<UserOutlined />}>
        {user?.username}
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
        退出登录
      </Menu.Item>
    </Menu>
  );

  const menuItems = [
    {
      key: '/admin',
      icon: <DashboardOutlined />,
      label: <Link to="/admin">概览</Link>,
    },
    {
      key: '/admin/coupons',
      icon: <CopyrightOutlined />,
      label: <Link to="/admin/coupons">核销管理</Link>,
    },
    {
      key: '/admin/sales',
      icon: <TeamOutlined />,
      label: <Link to="/admin/sales">销售管理</Link>,
    },
    {
      key: '/admin/records',
      icon: <HistoryOutlined />,
      label: <Link to="/admin/records">核销记录</Link>,
    },
    {
      key: '/admin/statistics',
      icon: <BarChartOutlined />,
      label: <Link to="/admin/statistics">核销统计</Link>,
    },
    {
      key: '/admin/settings',
      icon: <SettingOutlined />,
      label: <Link to="/admin/settings">系统设置</Link>,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider 
        collapsible 
        collapsed={collapsed} 
        onCollapse={setCollapsed}
        theme="light"
        width={250}
      >
        <div style={{ 
          padding: '16px', 
          textAlign: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
            {collapsed ? '核销' : '优惠券核销系统'}
          </Title>
        </div>
        <Menu
          theme="light"
          selectedKeys={[getSelectedKey()]}
          mode="inline"
          items={menuItems}
          style={{ borderRight: 0 }}
        />
      </Sider>
      
      <Layout>
        <Header style={{ 
          background: '#fff', 
          padding: '0 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <Title level={3} style={{ margin: 0, color: '#333' }}>
            超级管理员控制台
          </Title>
          
          <Dropdown overlay={userMenu} trigger={['click']}>
            <Button type="text" style={{ height: 'auto', padding: '4px 8px' }}>
              <Avatar size="small" icon={<UserOutlined />} />
              <span style={{ marginLeft: 8 }}>{user?.username}</span>
            </Button>
          </Dropdown>
        </Header>
        
        <Content style={{ margin: '24px', background: '#fff' }}>
          <Routes>
            <Route index element={<AdminOverview />} />
            <Route path="coupons" element={<CouponManagement />} />
            <Route path="sales" element={<SalesManagement />} />
            <Route path="records" element={<RedemptionRecords />} />
            <Route path="statistics" element={<Statistics />} />
            <Route path="settings" element={<Settings />} />
          </Routes>
        </Content>
        <Footer />
      </Layout>
    </Layout>
  );
};

const AdminOverview: React.FC = () => {
  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>概览</Title>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '24px',
        marginTop: '24px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '24px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <Title level={4} style={{ color: 'white', margin: 0 }}>优惠券管理</Title>
          <p>创建和管理优惠券</p>
        </div>
        
        <div style={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          padding: '24px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <Title level={4} style={{ color: 'white', margin: 0 }}>销售管理</Title>
          <p>管理销售员账户</p>
        </div>
        
        <div style={{
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white',
          padding: '24px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <Title level={4} style={{ color: 'white', margin: 0 }}>核销记录</Title>
          <p>查看所有核销记录</p>
        </div>
        
        <div style={{
          background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          color: 'white',
          padding: '24px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <Title level={4} style={{ color: 'white', margin: 0 }}>统计分析</Title>
          <p>核销数据统计</p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;